// Listado de TODOS los usuarios registrados (profiles).
// Muestra: contacto + role + cuántas suscripciones activas + cuántos pedidos one-shot.
// Filtros: todos | sin compras | suscriptores | compradores one-shot.

import { useEffect, useMemo, useState } from 'react'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'

const ROLE_LABEL = {
  admin: '🛡️ Admin',
  repartidor: '🚐 Repartidor',
  portero: '🛎️ Portero',
  cliente: '👤 Cliente',
}

const ROLE_COLOR = {
  admin: { bg: '#fee2e2', color: '#991b1b' },
  repartidor: { bg: '#dbeafe', color: '#1e40af' },
  portero: { bg: '#fef3c7', color: '#92400e' },
  cliente: { bg: '#f1f5f9', color: '#475569' },
}

const fmtFecha = (d) => d ? new Date(d).toLocaleDateString('es-UY', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'

export default function Usuarios() {
  const [usuarios, setUsuarios] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [filtro, setFiltro] = useState('todos')   // todos | sin_compras | suscriptores | one_shot
  const [busqueda, setBusqueda] = useState('')

  useEffect(() => { cargar() }, [])

  async function cargar() {
    setLoading(true)
    setError(null)
    try {
      // 1) Profiles
      const { data: profiles, error: pe } = await supabase
        .from('profiles')
        .select('id, nombre, email, phone, documento, documento_tipo, role, created_at')
        .order('created_at', { ascending: false })
        .limit(500)
      if (pe) throw pe

      // 2) Suscripciones (clientes) — agrupadas por titular_id y beneficiario_id
      const { data: subs, error: se } = await supabase
        .from('clientes')
        .select('id, estado, titular_id, beneficiario_id')
      if (se) throw se

      // 3) Pedidos one-shot — pedidos que no son de suscripción
      //    Algunos pedidos pueden no tener cliente_id (visitor). Los ignoramos para conteo por usuario.
      const { data: peds, error: pee } = await supabase
        .from('pedidos')
        .select('id, estado, tipo, comprador_profile_id, monto_total')
        .in('tipo', ['compra_unica', 'productos_sueltos'])
      if (pee) throw pee

      // 4) Mapas de conteo
      const subsActivasPorTitular = new Map()
      const subsPorPersona = new Map()  // cuenta cualquier rol (titular o beneficiario)
      ;(subs || []).forEach(s => {
        if (s.titular_id) {
          subsPorPersona.set(s.titular_id, (subsPorPersona.get(s.titular_id) || 0) + 1)
          if (s.estado === 'activa') {
            subsActivasPorTitular.set(s.titular_id, (subsActivasPorTitular.get(s.titular_id) || 0) + 1)
          }
        }
        if (s.beneficiario_id) {
          subsPorPersona.set(s.beneficiario_id, (subsPorPersona.get(s.beneficiario_id) || 0) + 1)
        }
      })

      const pedidosPorComprador = new Map()
      const pedidosPagadosPorComprador = new Map()
      const montoPorComprador = new Map()
      ;(peds || []).forEach(p => {
        if (!p.comprador_profile_id) return
        pedidosPorComprador.set(p.comprador_profile_id, (pedidosPorComprador.get(p.comprador_profile_id) || 0) + 1)
        if (p.estado === 'pagado' || p.estado === 'entregado' || p.estado === 'en_armado' || p.estado === 'en_ruta') {
          pedidosPagadosPorComprador.set(p.comprador_profile_id, (pedidosPagadosPorComprador.get(p.comprador_profile_id) || 0) + 1)
          montoPorComprador.set(p.comprador_profile_id, (montoPorComprador.get(p.comprador_profile_id) || 0) + Number(p.monto_total || 0))
        }
      })

      // 5) Construir filas
      const filas = (profiles || []).map(p => ({
        ...p,
        subs_total: subsPorPersona.get(p.id) || 0,
        subs_activas: subsActivasPorTitular.get(p.id) || 0,
        pedidos_total: pedidosPorComprador.get(p.id) || 0,
        pedidos_pagados: pedidosPagadosPorComprador.get(p.id) || 0,
        monto_one_shot: montoPorComprador.get(p.id) || 0,
      }))

      setUsuarios(filas)
    } catch (e) {
      setError(e.message || String(e))
    } finally {
      setLoading(false)
    }
  }

  const filtrados = useMemo(() => {
    let r = usuarios
    if (filtro === 'sin_compras') {
      r = r.filter(u => u.subs_total === 0 && u.pedidos_pagados === 0)
    } else if (filtro === 'suscriptores') {
      r = r.filter(u => u.subs_activas > 0)
    } else if (filtro === 'one_shot') {
      r = r.filter(u => u.pedidos_pagados > 0 && u.subs_activas === 0)
    }
    if (busqueda.trim()) {
      const q = busqueda.toLowerCase()
      r = r.filter(u =>
        u.nombre?.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q) ||
        u.phone?.toLowerCase().includes(q) ||
        (u.documento || '').toLowerCase().includes(q)
      )
    }
    return r
  }, [usuarios, filtro, busqueda])

  const counts = useMemo(() => ({
    todos: usuarios.length,
    sin_compras: usuarios.filter(u => u.subs_total === 0 && u.pedidos_pagados === 0).length,
    suscriptores: usuarios.filter(u => u.subs_activas > 0).length,
    one_shot: usuarios.filter(u => u.pedidos_pagados > 0 && u.subs_activas === 0).length,
  }), [usuarios])

  return (
    <AdminLayout>
      <div className="page">
        <h1>👤 Usuarios</h1>
        <p className="muted">Todas las personas registradas en la plataforma. Suscriptores, compradores one-shot y leads sin compra.</p>

        {/* Stat cards */}
        <div className="cards-grid" style={{ marginBottom: 16 }}>
          <div className="card">
            <h3>Total registrados</h3>
            <div className="stat">{counts.todos}</div>
          </div>
          <div className="card">
            <h3>Suscriptores activos</h3>
            <div className="stat" style={{ color: '#10B981' }}>{counts.suscriptores}</div>
          </div>
          <div className="card">
            <h3>One-shot</h3>
            <div className="stat" style={{ color: '#3b82f6' }}>{counts.one_shot}</div>
          </div>
          <div className="card">
            <h3>Sin compras</h3>
            <div className="stat" style={{ color: '#f59e0b' }}>{counts.sin_compras}</div>
          </div>
        </div>

        {/* Filtros */}
        <div className="ap-filtros" style={{ marginBottom: 12 }}>
          {[
            { v: 'todos',        label: 'Todos',                c: counts.todos },
            { v: 'suscriptores', label: '🎁 Suscriptores',       c: counts.suscriptores },
            { v: 'one_shot',     label: '🛍️ One-shot',          c: counts.one_shot },
            { v: 'sin_compras',  label: '🌱 Sin compras (leads)', c: counts.sin_compras },
          ].map(o => (
            <button key={o.v}
              className={`ap-filtro ${filtro === o.v ? 'is-active' : ''}`}
              onClick={() => setFiltro(o.v)}>
              {o.label} <em>{o.c}</em>
            </button>
          ))}
        </div>

        {/* Buscador */}
        <input
          type="search"
          placeholder="Buscar por nombre, email, teléfono o documento…"
          value={busqueda}
          onChange={e => setBusqueda(e.target.value)}
          style={{
            width: '100%', padding: '10px 14px', marginBottom: 16,
            border: '1px solid #d1d5db', borderRadius: 10, fontSize: 14,
          }}
        />

        {error && <div style={{ padding: 12, background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, color: '#991b1b', marginBottom: 12 }}>{error}</div>}
        {loading && <div className="ap-loading">Cargando…</div>}

        {!loading && filtrados.length === 0 && (
          <div className="ap-empty">
            <div style={{ fontSize: 48 }}>🔍</div>
            <h3>Sin resultados</h3>
            <p>Probá ajustar los filtros o limpiar la búsqueda.</p>
          </div>
        )}

        {!loading && filtrados.length > 0 && (
          <div style={{ overflowX: 'auto', background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb' }}>
            <table className="usu-table">
              <thead>
                <tr>
                  <th>Usuario</th>
                  <th>Contacto</th>
                  <th>Doc.</th>
                  <th>Rol</th>
                  <th style={{ textAlign: 'center' }}>Suscrip.</th>
                  <th style={{ textAlign: 'center' }}>One-shot</th>
                  <th style={{ textAlign: 'right' }}>$ One-shot</th>
                  <th>Alta</th>
                </tr>
              </thead>
              <tbody>
                {filtrados.map(u => {
                  const rc = ROLE_COLOR[u.role] || ROLE_COLOR.cliente
                  return (
                    <tr key={u.id}>
                      <td>
                        <div style={{ fontWeight: 700, color: '#1a3461' }}>{u.nombre || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>Sin nombre</span>}</div>
                      </td>
                      <td>
                        {u.email && <div style={{ fontSize: 12, color: '#374151' }}>📧 {u.email}</div>}
                        {u.phone && <div style={{ fontSize: 12, color: '#374151' }}>📱 {u.phone}</div>}
                      </td>
                      <td style={{ fontSize: 12, color: '#6b7280' }}>
                        {u.documento ? `${(u.documento_tipo || '').toUpperCase()} ${u.documento}` : '—'}
                      </td>
                      <td>
                        <span className="usu-pill" style={{ background: rc.bg, color: rc.color }}>
                          {ROLE_LABEL[u.role] || u.role}
                        </span>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {u.subs_activas > 0
                          ? <span style={{ display: 'inline-block', minWidth: 22, padding: '2px 8px', background: '#d1fae5', color: '#065f46', borderRadius: 999, fontSize: 12, fontWeight: 700 }}>{u.subs_activas}</span>
                          : <span style={{ color: '#cbd5e1' }}>—</span>}
                        {u.subs_total > u.subs_activas && (
                          <div style={{ fontSize: 10, color: '#94a3b8' }}>+{u.subs_total - u.subs_activas} no act.</div>
                        )}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {u.pedidos_pagados > 0
                          ? <span style={{ display: 'inline-block', minWidth: 22, padding: '2px 8px', background: '#dbeafe', color: '#1e40af', borderRadius: 999, fontSize: 12, fontWeight: 700 }}>{u.pedidos_pagados}</span>
                          : <span style={{ color: '#cbd5e1' }}>—</span>}
                      </td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontSize: 13, color: '#374151' }}>
                        {u.monto_one_shot > 0 ? `$${Number(u.monto_one_shot).toLocaleString('es-UY')}` : '—'}
                      </td>
                      <td style={{ fontSize: 12, color: '#6b7280' }}>{fmtFecha(u.created_at)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <style>{`
        .usu-table { width: 100%; border-collapse: collapse; font-size: 14px; }
        .usu-table thead th {
          text-align: left; padding: 12px 14px;
          background: #f8fafc; border-bottom: 1px solid #e5e7eb;
          font-size: 12px; text-transform: uppercase; color: #6b7280;
          font-weight: 700; letter-spacing: .03em;
        }
        .usu-table tbody td {
          padding: 12px 14px; border-bottom: 1px solid #f1f5f9; vertical-align: top;
        }
        .usu-table tbody tr:hover { background: #f8fafc; }
        .usu-pill {
          display: inline-block; padding: 3px 10px;
          border-radius: 999px; font-size: 11px; font-weight: 700;
        }
        .ap-filtros { display: flex; gap: 6px; flex-wrap: wrap; }
        .ap-filtro {
          padding: 8px 14px; border: 1px solid #e5e7eb; background: #fff;
          border-radius: 999px; cursor: pointer; font-size: 13px; font-weight: 600;
          color: #475569; display: inline-flex; align-items: center; gap: 6px;
        }
        .ap-filtro em { font-style: normal; opacity: .7; font-size: 11px; }
        .ap-filtro.is-active { background: #1a3461; color: #fff; border-color: #1a3461; }
        .ap-filtro:hover:not(.is-active) { background: #f1f5f9; }
        .ap-loading { padding: 40px; text-align: center; color: #6b7280; }
        .ap-empty { padding: 60px 20px; text-align: center; color: #6b7280; }
        .ap-empty h3 { color: #1a3461; margin: 16px 0 8px; }
      `}</style>
    </AdminLayout>
  )
}
